import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined, CloudSyncOutlined, EditOutlined, StopOutlined, CheckOutlined } from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import { withDevModalFilePath, NumericInput } from '../../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/admin/subscriptions/PlanMasterPage.tsx');
import { useAdminAccess } from '../../../hooks/useAdminAccess';

type BillingCycle = 'monthly' | 'quarterly' | 'yearly';
type PlanFeatureFlags = Record<string, string | number | boolean>;

interface SubscriptionPlan {
  id: string;
  planCode: string;
  displayName: string;
  description?: string | null;
  amount?: string | number | null;
  currency: string;
  billingCycle: BillingCycle;
  isDefault: boolean;
  isArchived: boolean;
  featureFlags?: PlanFeatureFlags | null;
  sortOrder?: string | number | null;
}

interface PlanFormValues {
  planCode?: string;
  displayName: string;
  description?: string;
  amount: number;
  currency: string;
  billingCycle: BillingCycle;
  isDefault?: boolean;
  featureFlags?: Array<{ key: string; value: string }>;
  sortOrder?: number;
}

const isBillingCycle = (value: unknown): value is BillingCycle =>
  value === 'monthly' || value === 'quarterly' || value === 'yearly';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeFeatureFlags = (value: unknown): PlanFeatureFlags => {
  if (!isRecord(value)) {
    return {};
  }

  return Object.entries(value).reduce<PlanFeatureFlags>((acc, [key, flagValue]) => {
    if (
      typeof flagValue === 'string' ||
      typeof flagValue === 'number' ||
      typeof flagValue === 'boolean'
    ) {
      acc[key] = flagValue;
    }
    return acc;
  }, {});
};

const normalizeSubscriptionPlan = (value: unknown): SubscriptionPlan | null => {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.planCode !== 'string' ||
    typeof value.displayName !== 'string' ||
    typeof value.currency !== 'string'
  ) {
    return null;
  }

  return {
    id: value.id,
    planCode: value.planCode,
    displayName: value.displayName,
    description: typeof value.description === 'string' ? value.description : null,
    amount: typeof value.amount === 'string' || typeof value.amount === 'number' ? value.amount : null,
    currency: value.currency,
    billingCycle: isBillingCycle(value.billingCycle) ? value.billingCycle : 'monthly',
    isDefault: typeof value.isDefault === 'boolean' ? value.isDefault : false,
    isArchived: typeof value.isArchived === 'boolean' ? value.isArchived : false,
    featureFlags: normalizeFeatureFlags(value.featureFlags),
    sortOrder: typeof value.sortOrder === 'string' || typeof value.sortOrder === 'number' ? value.sortOrder : null,
  };
};

const normalizeSubscriptionPlanList = (value: unknown): SubscriptionPlan[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((plan) => {
    const normalized = normalizeSubscriptionPlan(plan);
    return normalized ? [normalized] : [];
  });
};

function mapPlanToForm(plan: SubscriptionPlan): PlanFormValues {
  const featureFlagsEntries = Object.entries(plan.featureFlags ?? {});
  return {
    planCode: plan.planCode,
    displayName: plan.displayName,
    description: plan.description ?? undefined,
    amount: Number(plan.amount ?? 0),
    currency: plan.currency,
    billingCycle: plan.billingCycle,
    isDefault: plan.isDefault,
    featureFlags: featureFlagsEntries.map(([key, value]) => ({ key, value: String(value) })),
    sortOrder: plan.sortOrder ? Number(plan.sortOrder) : undefined,
  };
}

function mapFormToPayload(values: PlanFormValues) {
  const featureFlags = (values.featureFlags ?? []).reduce<PlanFeatureFlags>((acc, flag) => {
    if (flag.key.trim().length > 0) {
      acc[flag.key.trim()] = flag.value === 'true' ? true : flag.value === 'false' ? false : flag.value;
    }
    return acc;
  }, {});

  return {
    displayName: values.displayName,
    description: values.description ?? null,
    amount: values.amount,
    currency: values.currency,
    billingCycle: values.billingCycle,
    isDefault: values.isDefault ?? false,
    featureFlags,
    sortOrder: values.sortOrder ?? 0,
  };
}

const BILLING_CYCLE_OPTIONS = [
  { value: 'monthly', label: '月次' },
  { value: 'quarterly', label: '四半期' },
  { value: 'yearly', label: '年次' },
];

const CURRENCY_OPTIONS = [
  { value: 'JPY', label: 'JPY' },
  { value: 'USD', label: 'USD' },
];

export function PlanMasterPage() {
  const { requireRole } = useAdminAccess();
  const [form] = Form.useForm<PlanFormValues>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const listQuery = trpc.planMasterAdmin.list.useQuery({ includeArchived });
  const plans = useMemo(
    () => normalizeSubscriptionPlanList(listQuery.data),
    [listQuery.data]
  );
  const createMutation = trpc.planMasterAdmin.create.useMutation({
    onSuccess: () => {
      messageApi.success('プランを作成しました');
      setModalOpen(false);
      listQuery.refetch();
    },
    onError: (error) => messageApi.error(error?.message ?? 'プランの作成に失敗しました'),
  });
  const updateMutation = trpc.planMasterAdmin.update.useMutation({
    onSuccess: () => {
      messageApi.success('プランを更新しました');
      setModalOpen(false);
      setEditingPlan(null);
      listQuery.refetch();
    },
    onError: (error) => messageApi.error(error?.message ?? 'プランの更新に失敗しました'),
  });
  const archiveMutation = trpc.planMasterAdmin.setArchived.useMutation({
    onSuccess: () => {
      messageApi.success('プランの公開状態を更新しました');
      listQuery.refetch();
    },
    onError: (error) => messageApi.error(error?.message ?? '更新に失敗しました'),
  });
  const syncMutation = trpc.planMasterAdmin.syncExternal.useMutation({
    onSuccess: () => messageApi.success('外部課金プロバイダーと同期しました'),
    onError: (error) => messageApi.error(error?.message ?? '同期に失敗しました'),
  });

  const openCreateModal = () => {
    setEditingPlan(null);
    setModalOpen(true);
  };

  const openEditModal = useCallback((plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setModalOpen(true);
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    if (editingPlan) {
      form.setFieldsValue(mapPlanToForm(editingPlan));
      return;
    }

    form.resetFields();
    form.setFieldsValue({ currency: 'JPY', billingCycle: 'monthly', amount: 0, featureFlags: [] });
  }, [modalOpen, editingPlan, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields() as PlanFormValues;
      if (editingPlan) {
        await updateMutation.mutateAsync({ id: editingPlan.id, data: mapFormToPayload(values) });
      } else {
        await createMutation.mutateAsync({
          planCode: values.planCode!,
          displayName: values.displayName,
          description: values.description,
          amount: values.amount,
          currency: values.currency,
          billingCycle: values.billingCycle,
          isDefault: values.isDefault ?? false,
          featureFlags: mapFormToPayload(values).featureFlags,
          sortOrder: values.sortOrder ?? 0,
          metadata: {},
          externalProductId: null,
          externalPriceId: null,
        });
      }
    } catch {
      // validation error
    }
  };

  const toggleArchive = useCallback((plan: SubscriptionPlan) => {
    archiveMutation.mutate({ id: plan.id, archived: !plan.isArchived });
  }, [archiveMutation]);

  const allowed = requireRole(['system_admin', 'finance']);
  const columns = useMemo<ColumnsType<SubscriptionPlan>>(
    () => [
      {
        title: 'プランコード',
        dataIndex: 'planCode',
        key: 'planCode',
        render: (code: string) => <Tag>{code}</Tag>,
      },
      {
        title: '表示名',
        dataIndex: 'displayName',
        key: 'displayName',
      },
      {
        title: '金額',
        dataIndex: 'amount',
        key: 'amount',
        render: (amount: SubscriptionPlan['amount']) => `¥${Number(amount ?? 0).toLocaleString()}`,
      },
      {
        title: '課金サイクル',
        dataIndex: 'billingCycle',
        key: 'billingCycle',
        render: (cycle: string) => BILLING_CYCLE_OPTIONS.find((option) => option.value === cycle)?.label ?? cycle,
      },
      {
        title: 'ステータス',
        key: 'status',
        render: (_: unknown, plan) => (
          <Space>
            {plan.isDefault && <Tag color="green">デフォルト</Tag>}
            {plan.isArchived && <Tag color="volcano">非公開</Tag>}
          </Space>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        render: (_: unknown, plan) => (
          <Space>
            <Button icon={<EditOutlined />} onClick={() => openEditModal(plan)}>編集</Button>
            <Button
              icon={plan.isArchived ? <CheckOutlined /> : <StopOutlined />}
              onClick={() => toggleArchive(plan)}
              loading={archiveMutation.isPending}
            >
              {plan.isArchived ? '公開' : '非公開'}
            </Button>
          </Space>
        ),
      },
    ],
    [archiveMutation.isPending, openEditModal, toggleArchive],
  );

  if (!allowed) {
    return <Card>このページにアクセスする権限がありません</Card>;
  }

  return (
    <Card title="プランマスタ管理" extra={<Space>
      <Switch checked={includeArchived} onChange={setIncludeArchived} />
      <span>非公開プランを表示</span>
    </Space>}>
      {contextHolder}
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                プラン追加
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => listQuery.refetch()} loading={listQuery.isFetching}>
                再読込
              </Button>
            </Space>
          </Col>
          <Col>
            <Button icon={<CloudSyncOutlined />} onClick={() => syncMutation.mutate()} loading={syncMutation.isPending}>
              Stripe同期
            </Button>
          </Col>
        </Row>

        <Table<SubscriptionPlan>
          columns={columns}
          dataSource={plans}
          rowKey="id"
          loading={listQuery.isLoading}
        />
      </Space>

      <Modal
        title={editingPlan ? 'プラン編集' : 'プラン作成'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditingPlan(null);
        }}
        onOk={handleSubmit}
        okButtonProps={{ loading: createMutation.isPending || updateMutation.isPending }}
        width={640}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          {!editingPlan && (
            <Form.Item
              name="planCode"
              label="プランコード"
              rules={[{ required: true, message: 'プランコードを入力してください' }]}
            >
              <Input maxLength={64} placeholder="starter" />
            </Form.Item>
          )}
          <Form.Item
            name="displayName"
            label="表示名"
            rules={[{ required: true, message: '表示名を入力してください' }]}
          >
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item name="description" label="説明">
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="amount" label="金額" rules={[{ required: true, message: '金額を入力してください' }]}>
                <NumericInput min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label="通貨" initialValue="JPY">
                <Select options={CURRENCY_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="billingCycle" label="課金サイクル" initialValue="monthly">
                <Select options={BILLING_CYCLE_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="isDefault" label="デフォルト">
                <Switch checkedChildren="はい" unCheckedChildren="いいえ" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sortOrder" label="表示順">
                <NumericInput min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.List name="featureFlags">
            {(fields, { add, remove }) => (
              <Card size="small" title="機能フラグ" extra={<Button onClick={() => add({ key: '', value: 'true' })}>追加</Button>}>
                {fields.length === 0 && <span>フラグが設定されていません</span>}
                <Space direction="vertical" style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline">
                      <Form.Item
                        {...field}
                        name={[field.name, 'key']}
                        fieldKey={[field.fieldKey!, 'key']}
                        rules={[{ required: true, message: 'キーを入力してください' }]}
                      >
                        <Input placeholder="feature_key" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, 'value']}
                        fieldKey={[field.fieldKey!, 'value']}
                        initialValue="true"
                      >
                        <Select style={{ width: 120 }}>
                          <Select.Option value="true">true</Select.Option>
                          <Select.Option value="false">false</Select.Option>
                        </Select>
                      </Form.Item>
                      <Button danger onClick={() => remove(field.name)}>
                        削除
                      </Button>
                    </Space>
                  ))}
                </Space>
              </Card>
            )}
          </Form.List>
        </Form>
      </Modal>
    </Card>
  );
}


